<script setup lang="ts">
import {
  Target,
  Network,
  FileCode,
  BarChart3,
  ClipboardList,
  SearchCode,
  BookOpen,
  Sparkles,
} from 'lucide-vue-next'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { CopilotTemplate, CopilotTemplateCategory } from '@/types'

const emit = defineEmits<{
  select: [template: CopilotTemplate]
}>()

const COPILOT_TEMPLATES: CopilotTemplate[] = [
  {
    id: 'scope',
    category: 'scope',
    title: 'Documento de Alcance',
    description: 'Genera un documento de alcance con objetivos, limites y entregables',
    promptTemplate:
      'Genera un documento de alcance completo para el proyecto basandote en el contexto proporcionado. Incluye: Objetivos, Alcance (dentro y fuera), Entregables, Supuestos, Restricciones y Criterios de Aceptacion.',
    icon: 'Target',
    requiredContextTypes: ['requirement'],
  },
  {
    id: 'architecture',
    category: 'architecture',
    title: 'Documento de Arquitectura',
    description: 'Genera un documento de arquitectura tecnica con diagramas y decisiones',
    promptTemplate:
      'Genera un documento de arquitectura tecnica basandote en las aplicaciones y requerimientos del proyecto. Incluye: Vision General, Componentes, Tecnologias, Integraciones, Decisiones de Diseño y Diagramas (usa mermaid para diagramas).',
    icon: 'Network',
    requiredContextTypes: ['application', 'requirement'],
  },
  {
    id: 'specifications',
    category: 'specifications',
    title: 'Especificaciones Tecnicas',
    description: 'Genera especificaciones tecnicas detalladas a partir de los requerimientos',
    promptTemplate:
      'Genera un documento de especificaciones tecnicas detalladas. Para cada requerimiento seleccionado, incluye: Descripcion funcional, Flujos de usuario, Reglas de negocio, Criterios de aceptacion, y Consideraciones tecnicas.',
    icon: 'FileCode',
    requiredContextTypes: ['requirement'],
  },
  {
    id: 'status_report',
    category: 'status_report',
    title: 'Reporte de Estado',
    description: 'Genera un reporte de estado del proyecto con metricas y progreso',
    promptTemplate:
      'Genera un reporte de estado del proyecto. Incluye: Resumen Ejecutivo, Progreso de Requerimientos (por estado), Metricas clave, Riesgos identificados, Proximos pasos y Reuniones recientes.',
    icon: 'BarChart3',
    requiredContextTypes: ['requirement', 'meeting'],
  },
  {
    id: 'meeting_minutes',
    category: 'meeting_minutes',
    title: 'Acta de Reunion',
    description: 'Genera un acta de reunion formateada a partir de notas y decisiones',
    promptTemplate:
      'Genera un acta de reunion formal basandote en la informacion de reuniones proporcionada. Incluye: Datos de la reunion, Asistentes, Agenda, Puntos tratados, Decisiones tomadas, Accionables con responsables y fechas.',
    icon: 'ClipboardList',
    requiredContextTypes: ['meeting'],
  },
  {
    id: 'technical_review',
    category: 'technical_review',
    title: 'Revision Tecnica',
    description: 'Genera un reporte de revision tecnica de aplicaciones',
    promptTemplate:
      'Genera un reporte de revision tecnica de las aplicaciones del proyecto. Incluye: Estado actual, Stack tecnologico, Version actual, Deuda tecnica identificada, Recomendaciones de mejora, y Plan de actualizacion.',
    icon: 'SearchCode',
    requiredContextTypes: ['application'],
  },
  {
    id: 'manual',
    category: 'manual',
    title: 'Manual de Usuario',
    description: 'Genera un manual de usuario basado en los requerimientos y aplicaciones',
    promptTemplate:
      'Genera un manual de usuario para la aplicacion. Incluye: Introduccion, Requisitos, Guia de inicio rapido, Funcionalidades principales (basado en requerimientos), Preguntas frecuentes.',
    icon: 'BookOpen',
    requiredContextTypes: ['requirement', 'application'],
  },
  {
    id: 'custom',
    category: 'custom',
    title: 'Documento Personalizado',
    description: 'Conversa libremente con el copiloto para generar cualquier documento',
    promptTemplate: '',
    icon: 'Sparkles',
  },
]

const iconMap: Record<string, unknown> = {
  Target,
  Network,
  FileCode,
  BarChart3,
  ClipboardList,
  SearchCode,
  BookOpen,
  Sparkles,
}

const getIcon = (iconName: string) => {
  return iconMap[iconName] || Sparkles
}

const getCategoryColor = (category: CopilotTemplateCategory): string => {
  const colors: Record<CopilotTemplateCategory, string> = {
    scope: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    architecture: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    specifications: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    manual: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    status_report: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    meeting_minutes: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
    technical_review: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    custom: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  }
  return colors[category] || colors.custom
}
</script>

<template>
  <div class="grid grid-cols-2 lg:grid-cols-3 gap-3 p-4">
    <Card
      v-for="template in COPILOT_TEMPLATES"
      :key="template.id"
      class="cursor-pointer transition-all hover:shadow-md hover:border-primary/50"
      :class="{ 'col-span-2 lg:col-span-3 border-dashed': template.id === 'custom' }"
      @click="emit('select', template)"
    >
      <CardContent class="p-4">
        <div class="flex items-start gap-3">
          <div class="p-2 rounded-lg bg-muted shrink-0">
            <component :is="getIcon(template.icon)" class="h-5 w-5" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="font-medium text-sm">{{ template.title }}</p>
            <p class="text-xs text-muted-foreground mt-1 line-clamp-2">
              {{ template.description }}
            </p>
            <div v-if="template.requiredContextTypes?.length" class="flex flex-wrap gap-1 mt-2">
              <Badge
                v-for="ctx in template.requiredContextTypes"
                :key="ctx"
                variant="secondary"
                :class="getCategoryColor(template.category)"
                class="text-[10px] px-1.5 py-0"
              >
                {{ ctx }}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
</template>
